
import { defineComponent, ref, reactive } from 'vue';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import { POST_BROADCAST_MEMBER, IMPORT_BROADCAST_MEMBERS } from '@/store/broadcast.module';
import type { ApiErrorResponse, BroadcastRecipientPayload } from '@/types/domain';

interface BroadcastImportResult {
  inserted: number;
  skipped: number;
  total: number;
}

export default defineComponent({
  setup(_props, { emit }) {
    const store = useStore();
    const isLoading = ref(false);
    const mode = ref<'manual' | 'import'>('manual');
    const fileInput = ref<HTMLInputElement | null>(null);
    const selectedFile = ref<File | null>(null);

    const form = reactive<BroadcastRecipientPayload>({
      name: '',
      nim: '',
      noWhatsapp: '',
      email: '',
    });

    const closeModal = () => emit('close');

    const submitManual = async () => {
      const errors: string[] = [];
      if (!form.name.trim()) errors.push('Nama wajib diisi.');
      if (!String(form.noWhatsapp || '').trim() && !String(form.email || '').trim()) errors.push('Minimal salah satu dari No. WhatsApp atau Email wajib diisi.');
      if (errors.length) {
        Swal.fire({ title: 'Validasi', html: errors.join('<br>'), icon: 'warning', confirmButtonColor: '#003793' });
        return;
      }
      isLoading.value = true;
      try {
        await store.dispatch(POST_BROADCAST_MEMBER, { ...form });
        emit('close', { added: 1 });
      } catch (err: unknown) {
        const apiError = err as ApiErrorResponse;
        Swal.fire({ title: 'Error', text: apiError?.response?.data?.message || apiError?.message || 'Gagal menyimpan.', icon: 'error', confirmButtonColor: '#003793' });
        isLoading.value = false;
      }
    };

    const onFileChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      selectedFile.value = target.files && target.files[0] ? target.files[0] : null;
    };

    const submitImport = async () => {
      if (!selectedFile.value) return;
      isLoading.value = true;
      try {
        const result = await store.dispatch(IMPORT_BROADCAST_MEMBERS, selectedFile.value) as BroadcastImportResult;
        Swal.fire({
          title: 'Berhasil',
          text: `${result?.inserted ?? 0} penerima ditambahkan${result?.skipped ? `, ${result.skipped} baris dilewati` : ''}.`,
          icon: 'success',
          confirmButtonColor: '#003793',
        });
        emit('close', { added: result?.inserted ?? 0 });
      } catch (err: unknown) {
        const apiError = err as ApiErrorResponse;
        Swal.fire({ title: 'Error', text: apiError?.response?.data?.message || apiError?.message || 'Gagal mengimpor.', icon: 'error', confirmButtonColor: '#003793' });
        isLoading.value = false;
      }
    };

    return { mode, form, isLoading, fileInput, selectedFile, closeModal, submitManual, submitImport, onFileChange };
  },
});
